import { AVATAR_QUALITY, AVATAR_SIZE, centreSquare, inspectAvatar } from '@fsv/core';
import { de } from '@fsv/core';

/** The square to keep, in pixels of the upright image (AvatarCropper). */
export type AvatarCrop = { readonly x: number; readonly y: number; readonly side: number };

/**
 * A profile picture as the API wants it: the chosen square (default: the
 * centre square) of the image, AVATAR_SIZE px, JPEG — re-encoded by the canvas, which writes no
 * EXIF, and checked with the server's own rule before it leaves the browser.
 * Answers base64 for `Backend.setAvatar`.
 */
export async function prepareAvatar(file: File, chosen?: AvatarCrop): Promise<string> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => null);
  if (!bitmap) throw new Error(de.avatar.refused.not_jpeg);
  const crop = chosen ?? centreSquare(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(de.avatar.refused.not_jpeg);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, crop.x, crop.y, crop.side, crop.side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', AVATAR_QUALITY));
  if (!blob) throw new Error(de.avatar.refused.not_jpeg);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const verdict = inspectAvatar(bytes);
  if (!verdict.ok) throw new Error(de.avatar.refused[verdict.reason]);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}
